import React, { useEffect, useId, useRef, useState } from "react";
import { signOut } from "firebase/auth";

import { auth } from "../../../firebase/firebase";
import { useAuth } from "../../../state/AuthContext";
import { AccountIcon } from "../../primatives/Icons";

export default function AccountMenu({
  onOpenSignIn,
  onOpenTimeline,
  onOpenHow,
  onOpenTerms,
  onOpenPrivacy,
  onOpenDonate,
  onOpenContact,
}) {
  const { isSignedIn, isPro, email } = useAuth();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const toastTimerRef = useRef(null);

  const close = () => setOpen(false);
  const toggle = () => setOpen((v) => !v);

  const showToast = (next) => {
    setToast(next);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 2500);
  };

  // Close on outside click / ESC
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t)) return;
      if (btnRef.current?.contains(t)) return;
      close();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Cleanup any pending timer on unmount
  useEffect(() => {
    return () => {
      window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const onClickSignIn = () => {
    close();
    onOpenSignIn?.();
  };

  const onClickSignOut = async () => {
    close();
    setBusy(true);
    try {
      await signOut(auth);
      showToast({ type: "ok", message: "Signed out." });
    } catch (e) {
      showToast({ type: "err", message: e?.message || "Sign out failed." });
    } finally {
      setBusy(false);
    }
  };

  const onClickHow = () => {
    close();
    onOpenHow?.();
  };

  const onClickTimeline = () => {
    close();
    onOpenTimeline?.();
  };

  const onClickTerms = () => {
    close();
    onOpenTerms?.();
  };

  const onClickPrivacy = () => {
    close();
    onOpenPrivacy?.();
  };

  const onClickDonate = () => {
    close();
    if (onOpenDonate) return onOpenDonate();
    window.open(
      "https://www.paypal.com/donate/?hosted_button_id=UHUNQVDR2S2X4",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const onClickContact = () => {
    close();
    if (onOpenContact) return onOpenContact();
    window.location.href = "mailto:support@diceframe.co.uk";
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-800 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-900"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title="Account"
      >
        <AccountIcon className="h-5 w-5" />
      </button>

      {/* Toast indicator (anchored to the account button) */}
      {toast ? (
        <div
          className={[
            "absolute right-0 mt-2 w-56 rounded-xl border px-3 py-2 text-xs shadow-lg",
            toast.type === "ok"
              ? "border-green-200 bg-green-50 text-green-800 dark:border-green-900/40 dark:bg-green-950/40 dark:text-green-200"
              : "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200",
          ].join(" ")}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          className="absolute right-0 mt-2 w-72 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-950"
        >
          {/* Signed-in header */}
          {isSignedIn ? (
            <div className="flex items-center gap-3 border-b border-neutral-200/70 px-4 py-3 dark:border-neutral-800/70">
              <div className="h-10 w-10 rounded-full border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900" />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {email ?? "Signed in"}
                </div>
                <div className="text-xs text-neutral-600 dark:text-neutral-400">
                  {isPro ? "Pro" : "Free"}
                </div>
              </div>
            </div>
          ) : (
            <div className="border-b border-neutral-200/70 px-4 py-3 text-sm text-neutral-700 dark:border-neutral-800/70 dark:text-neutral-300">
              Not signed in
            </div>
          )}

          {/* Menu items */}
          <div className="p-2">
            {!isSignedIn ? (
              <div className="mb-2 border-b border-green-200 dark:border-green-900/40 pb-2">
                <button
                  type="button"
                  role="menuitem"
                  onClick={onClickSignIn}
                  className="
                    w-full rounded-xl px-3 py-2 text-left text-sm font-semibold
                    bg-green-50 text-green-800
                    hover:bg-green-100
                    dark:bg-green-950/40 dark:text-green-200 dark:hover:bg-green-950/60
                  "
                >
                  Sign in
                </button>
              </div>
            ) : null}

            <button
              type="button"
              role="menuitem"
              onClick={onClickHow}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              How it’s done
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={onClickTimeline}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              Timeline
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={onClickDonate}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              Donate
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={onClickTerms}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              Terms
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={onClickPrivacy}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              Privacy
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={onClickContact}
              className="w-full rounded-xl px-3 py-2 text-left text-sm text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-900"
            >
              Contact
            </button>

            {isSignedIn ? (
              <>
                <div className="my-2 h-px bg-neutral-200 dark:bg-neutral-800" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={onClickSignOut}
                  disabled={busy}
                  className="w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  {busy ? "Signing out…" : "Sign out"}
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
